import { IdMap, CouplesMatchAvailability } from "../../api/types";

export type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";
export const daysArray: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export interface ClinicianManualCapacityV2Data {
  clinician: number;
  capacity: number;
  auto_capacity: number;
  couples_capacity?: number;
  couples_auto_capacity?: number;
  note?: string;
  block_biweekly: boolean;
  block_kp_clients: boolean;
  is_expired: boolean;
  set_at: string;
  _date_start: string;
}

export interface ClinicianVolumeData {
  clinician_id: number;
  weekly_volume_score: number;
  weekly_volume_score_with_pending_matches: number;
  biweekly_clients: number;
  weekly_clients: number;
  unknown_clients: number;
  biweekly_couples: number;
  weekly_couples: number;
  consult_allocation: number;
  couples_allocation: number;
  target_client_hours: number;
  remaining_volume: number;
  number_pending_matches: number;
  source:
    | "legacy_weekly_volume"
    | "tenured_weekly_volume_2023_10"
    | "ramp_beta";
  slots_data: SlotsData;
}

export interface LegacyVolumeData extends ClinicianVolumeData {
  source: "legacy_weekly_volume";
}
export interface ClinicianTenuredVolumeData extends ClinicianVolumeData {
  source: "tenured_weekly_volume_2023_10";
  mrmr_data: TenuredVolumeMetricData;
}

export interface TenuredVolumeMetricData {
  clinician: number;
  timestamp: string;
  weeks_tenure: number;
  weeks_1_out: number;
  weeks_2_out: number;
  weeks_3_out: number;
  weeks_4_out: number;
  pending_matches: number;
  pto_weeks: PtoWeeks;
  avg_4_weeks: number;
  recommended_matches_requested: number;
  target_scheduled_appointments: number;
}

export interface PtoWeeks {
  clinician_id: number;
  weeks_1_out: number | null;
  weeks_2_out: number | null;
  weeks_3_out: number | null;
  weeks_4_out: number | null;
}

export interface ClinicianRampVolumeData extends ClinicianVolumeData {
  source: "ramp_beta";
  ramp_data: RampMetricData;
}

export interface RampMetricData {
  timestamp: string;
  match_session_week_num: number;
  onboarding_start_date: string;
  clinician_id: number;
  full_name: string;
  weeks_1_out: number;
  weeks_2_out: number;
  weeks_3_out: number;
  weeks_4_out: number;
  weeks_1_out_pto_holiday: number;
  weeks_2_out_pto_holiday: number;
  weeks_3_out_pto_holiday: number;
  weeks_4_out_pto_holiday: number;
  pending_matches: number;
  pto_weeks: PtoWeeks;
  requesting_for_week: number;
  target_scheduled_appointments: number;
  avg_4_weeks: number;
  actual_scheduled: number;
  new_appointments_needed: number;
  has_pto_next_week: boolean;
  recommended_matches_requested: number;
}

export function volumeDataIsRamp(
  d: ClinicianVolumeData,
): d is ClinicianRampVolumeData {
  return d.source === "ramp_beta";
}

export function volumeDataIsTenured(
  d: ClinicianVolumeData,
): d is ClinicianTenuredVolumeData {
  return d.source === "tenured_weekly_volume_2023_10";
}

export function volumeDataIsLegacy(
  d: ClinicianVolumeData,
): d is LegacyVolumeData {
  return d.source === "legacy_weekly_volume";
}

export interface SlotsData {
  clinician_id: number;
  open_slots: OpenSlot[];
  errors: any[];
}

export interface OpenSlot {
  clinician_id: number;
  subregion_id: number;
  recurrence: Recurrence;
  start_time: string;
  day_of_week: string;
  start_date: string;
  note: string | null;
  type?: "manual" | string;
  id?: string;
}

export enum Recurrence {
  Biweekly = "biweekly",
  Weekly = "weekly",
}

export type ClinicianVolumeDataMap = IdMap<ClinicianVolumeData>;
export type ClinicianManualCapacityV2DataMap =
  IdMap<ClinicianManualCapacityV2Data>;
export type CouplesMatchAvailabilityMap = IdMap<CouplesMatchAvailability>;

export enum ReservationType {
  Consult = "consult",
  Couples = "couples_therapy",
  Individual = "individual",
}
